import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import TextLink from '@narvar/nth-text-link';
import Icon from '../Icon';
import { SAVE_ERROR, SAVE_SUCCESS } from '../../constants/preferences';
import { logData } from '../../actions/analytics';
import { EVENTS, EVENT_VALUES } from '../../constants/noflake/noflakeSchema';

const getStep4LanguageCode = ({ hasPreferences, saveState }) => {
  if (hasPreferences && !saveState) {
    return 'manage';
  }
  return saveState;
};

const Step2Container = styled.div`
  margin: 40px auto;
  text-align: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const Step4Container = styled.div`
  margin: 1rem auto 0 auto;
  text-align: center;
`;

const Step4Description = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const mapStateToProps = ({
  language,
  retailerSettings: { branding: { brand_call_to_action: brandCTAColor } = {} } = {},
  userPreferences: { enabled, applied, preferences, profile, saveState } = {},
  returnSubmission: { returnProcessInfo } = {},
}) => ({
  language,
  brandCTAColor,
  enabled,
  applied,
  saveState,
  hasPreferences:
    preferences && preferences.return_methods && preferences.return_methods.length > 0,
  hasProfile: Boolean(profile && profile.profile_id),
  returnComplete: !isEmpty(returnProcessInfo),
});

const PreferencesSaveState = ({
  dispatch,
  language,
  brandCTAColor,
  enabled,
  applied,
  saveState,
  hasPreferences,
  hasProfile,
  returnComplete,
}) => {
  if (!enabled || (!returnComplete && !applied)) return null;

  if (!returnComplete) {
    return (
      <Step2Container>
        <Icon.SuccessCheckmark width="15px" fill={brandCTAColor} />
        {language['returns.step_2.preferences.description']}
        <TextLink
          s={{
            color: brandCTAColor,
            hoverColor: brandCTAColor,
            fontFamily: 'inherit',
            textDecoration: 'underline',
            margin: '0px',
          }}
          target="_blank"
          href={process.env.NARVAR_PROFILE_URL}
          onClick={() => {
            dispatch(
              logData({
                type: EVENTS.CLICK,
                value: EVENT_VALUES[EVENTS.CLICK].preferences_manage_profile,
              }),
            );
          }}
        >
          {language['returns.step_2.preferences.link_text']}
        </TextLink>
      </Step2Container>
    );
  }

  const code = getStep4LanguageCode({
    hasPreferences,
    saveState,
  });

  if (!hasPreferences && hasProfile) {
    return (
      <Step4Container>
        <Step4Description>
          {language[`returns.step_4.preferences.update.description`]}
        </Step4Description>
        <div>
          <TextLink
            s={{
              color: brandCTAColor,
              hoverColor: brandCTAColor,
              fontWeight: 'bold',
              fontFamily: 'inherit',
              margin: '0px',
            }}
            target="_blank"
            href={process.env.NARVAR_PROFILE_URL}
            onClick={() => {
              dispatch(
                logData({
                  type: EVENTS.CLICK,
                  value: EVENT_VALUES[EVENTS.CLICK].preferences_update_profile,
                }),
              );
            }}
          >
            {language[`returns.step_4.preferences.update.link_text`]}
          </TextLink>
        </div>
      </Step4Container>
    );
  }

  if (hasPreferences || saveState === SAVE_SUCCESS) {
    return (
      <Step4Container>
        <Step4Description>
          <div>
            <Icon.PreferenceStatus saveState={SAVE_SUCCESS} />
          </div>
          {language[`returns.step_4.preferences.${code}.description`]}
        </Step4Description>
        <div>
          <TextLink
            s={{
              color: brandCTAColor,
              hoverColor: brandCTAColor,
              fontWeight: 'bold',
              fontFamily: 'inherit',
              margin: '0px',
            }}
            target="_blank"
            href={process.env.NARVAR_PROFILE_URL}
            onClick={() => {
              if (code === 'manage')
                dispatch(
                  logData({
                    type: EVENTS.CLICK,
                    value: EVENT_VALUES[EVENTS.CLICK].preferences_manage_profile,
                  }),
                );

              if (code === SAVE_SUCCESS)
                dispatch(
                  logData({
                    type: EVENTS.CLICK,
                    value: EVENT_VALUES[EVENTS.CLICK].preferences_complete_profile,
                  }),
                );

              if (code === SAVE_ERROR)
                dispatch(
                  logData({
                    type: EVENTS.CLICK,
                    value: EVENT_VALUES[EVENTS.CLICK].preferences_create_profile,
                  }),
                );
            }}
          >
            {language[`returns.step_4.preferences.${code}.link_text`]}
          </TextLink>
        </div>
      </Step4Container>
    );
  }

  return null;
};

PreferencesSaveState.defaultProps = {
  brandCTAColor: '#000',
  enabled: false,
  applied: false,
  saveState: undefined,
  hasPreferences: false,
  hasProfile: false,
  returnComplete: false,
};

PreferencesSaveState.propTypes = {
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.object.isRequired,
  brandCTAColor: PropTypes.string,
  enabled: PropTypes.boolean,
  applied: PropTypes.boolean,
  saveState: PropTypes.oneOf([SAVE_SUCCESS, SAVE_ERROR]),
  hasPreferences: PropTypes.boolean,
  hasProfile: PropTypes.boolean,
  returnComplete: PropTypes.boolean,
};

export default connect(mapStateToProps)(PreferencesSaveState);
